import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = 'invoice_footer_customisation';

const AvailableFooterFieldSchema = z.object({
  name: z.string().describe('Translated display name'),
  field: z.string().describe('Slugified name'),
  content: z.string(),
});

const FooterFieldSchema = z.union([
  z.object({
    field: z.string(),
    content: z.string(),
  }),
  z.string(),
  z.null(),
]);

export const InvoiceFooterSchema = z.object({
  availableFields: z.array(AvailableFooterFieldSchema),
  fields: z.array(FooterFieldSchema),
});

export type InvoiceFooterField = z.infer<typeof FooterFieldSchema>;
export type InvoiceFooter = z.infer<typeof InvoiceFooterSchema>;

export async function loadInvoiceFooterQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);
  return InvoiceFooterSchema.parse(data);
}

export function useLoadInvoiceFooter() {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<InvoiceFooter, ApiError>({
    queryKey: ['invoiceFooter'],
    queryFn: () => loadInvoiceFooterQueryFn(axiosInstance),
  });

  return addResourceNameToQueryResult<InvoiceFooter, ApiError, 'invoiceFooter'>(
    'invoiceFooter',
    queryResult,
  );
}
